// src/pages/Profile.js
// Page profil connectée au vrai statut d'authentification - NE PAS MODIFIER

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/shared/LanguageSwitcher';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
import { APP_CONFIG } from '../constants/config';

const Profile = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();

  const menuItems = [
    { icon: '⚙️', label: t('common.settings'), path: '/settings' },
    { icon: '💬', label: t('nav.messages'), path: '/messages' },
    { icon: '🔔', label: t('common.info'), path: '/notifications' },
    { icon: '❓', label: t('common.help'), path: '/support' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getInitial = () => {
    if (!user?.name) return '👤';
    return user.name.charAt(0).toUpperCase();
  };

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.bg },
    header: {
      padding: '16px',
      position: 'sticky',
      top: 0,
      backgroundColor: theme.bg,
      zIndex: 50,
      borderBottom: `1px solid ${theme.borderSoft}`,
    },
    title: { fontSize: '22px', fontWeight: '700', color: theme.textPrimary },
    profileCard: {
      margin: '16px',
      padding: '24px',
      backgroundColor: theme.bgCard,
      borderRadius: '16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px',
      border: `1px solid ${theme.border}`,
    },
    avatar: {
      width: '72px',
      height: '72px',
      borderRadius: '50%',
      backgroundColor: theme.goldSoft,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isAuthenticated ? '28px' : '32px',
      fontWeight: '700',
      color: theme.gold,
    },
    userName: { fontSize: '18px', fontWeight: '600', color: theme.textPrimary },
    userEmail: { fontSize: '13px', color: theme.textSecondary },
    guestText: { fontSize: '13px', color: theme.textSecondary, textAlign: 'center' },
    actionButton: {
      marginTop: '8px',
      padding: '12px 32px',
      backgroundColor: theme.gold,
      border: 'none',
      borderRadius: '10px',
      color: theme.mode === 'light' ? '#FFFFFF' : '#0A0E1A',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
    },
    logoutButton: {
      marginTop: '8px',
      padding: '12px 32px',
      backgroundColor: 'transparent',
      border: '1px solid rgba(245, 101, 101, 0.4)',
      borderRadius: '10px',
      color: '#F56565',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
    },
    section: {
      padding: '8px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    sectionTitle: {
      fontSize: '13px',
      fontWeight: '600',
      color: theme.textMuted,
      textTransform: 'uppercase',
      padding: '8px 0',
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '14px',
      backgroundColor: theme.bgCard,
      borderRadius: '10px',
      cursor: 'pointer',
      border: `1px solid ${theme.borderSoft}`,
    },
    menuIcon: { fontSize: '20px' },
    menuLabel: { flex: 1, fontSize: '15px', color: theme.textPrimary },
    menuArrow: { color: theme.textMuted, fontSize: '18px' },
    languageRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '14px',
      backgroundColor: theme.bgCard,
      borderRadius: '10px',
      border: `1px solid ${theme.borderSoft}`,
    },
    version: {
      textAlign: 'center',
      fontSize: '12px',
      color: theme.textMuted,
      padding: '24px',
    },
  };

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Mon profil | Fynho</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.header}>
        <div style={styles.title}>👤 {t('nav.profile')}</div>
      </div>

      <div style={styles.profileCard}>
        <div style={styles.avatar}>{isAuthenticated ? getInitial() : '👤'}</div>

        {isAuthenticated ? (
          <>
            <div style={styles.userName}>{user.name}</div>
            <div style={styles.userEmail}>{user.email}</div>
            <button style={styles.logoutButton} onClick={handleLogout}>
              {t('common.logout')}
            </button>
          </>
        ) : (
          <>
            <div style={styles.userName}>Invité</div>
            <div style={styles.guestText}>
              Connectez-vous pour sauvegarder vos favoris et suivre vos réservations
            </div>
            <button style={styles.actionButton} onClick={() => navigate('/login')}>
              Se connecter
            </button>
          </>
        )}
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Paramètres</div>
        <div style={styles.languageRow}>
          <span style={styles.menuLabel}>🌍 {t('common.language')}</span>
          <LanguageSwitcher compact />
        </div>
        {menuItems.map((item, index) => (
          <div key={index} style={styles.menuItem} onClick={() => navigate(item.path)}>
            <span style={styles.menuIcon}>{item.icon}</span>
            <span style={styles.menuLabel}>{item.label}</span>
            <span style={styles.menuArrow}>›</span>
          </div>
        ))}
      </div>

      <div style={styles.version}>
        Fynho v{APP_CONFIG.APP_VERSION}
      </div>
    </div>
  );
};

export default Profile;